import { Component, OnInit } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { ExcelImportService } from '../../../services/excel-import.service';
import {
  ColDef, GridApi, GridReadyEvent, RowClassParams, RowStyle,
  ClientSideRowModelModule, RowGroupingModule, ModuleRegistry, RowGroupingDisplayType, UseGroupTotalRow
} from 'ag-grid-enterprise';

import * as dayjs from 'dayjs';
import exportFromJSON from 'export-from-json';




type TemplateTypeDto = {
  template_id: number;
  template_name: string;
  import_table: string;
  description?: string;
  created_at?: string;
  created_by?: string;
};

@Component({
  selector: 'app-excel-upload',
  templateUrl: './excel-upload.component.html',
  styleUrls: ['./excel-upload.component.css'],
})
export class ExcelUploadComponent implements OnInit {

  private token: string | null = null;
  templateTypes: TemplateTypeDto[] = [];
  selectedTemplateId: number | null = 1;
  selectedTemplate?: TemplateTypeDto | null;
  selectedFile: File | null = null;
  fileName = '';
  isImporting = false;
  progress = -1;
  message = '';
  tableName = '';
  gridApi!: GridApi<any>;
  rowData: any[] = [];
  columnDefs: ColDef[] = [];
  columnDefsList: ColDef[] = [];
  rowSelection: any = "single";
  listBatches: any[] = [];

  constructor(private excelSvc: ExcelImportService) { }

  ngOnInit(): void {
    const userRaw = sessionStorage.getItem('wOSUser');
    try {
      this.token = userRaw ? JSON.parse(userRaw)?.stsTokenManager?.accessToken ?? null : null;
    } catch { this.token = null; }

    if (!this.token) {
      this.message = 'Auth token not found. Please sign in again.';
      console.warn('ExcelUploadComponent: no access token in sessionStorage.wOSUser');
    }

    this.excelSvc.getTemplateTypes(this.token ?? '')
      .pipe(take(1))
      .subscribe({
        next: (rows) => this.templateTypes = rows ?? [],
        error: (err) => {
          console.error('Failed to load template types', err);
          this.message = 'Could not load template types.';
        }
      });
  }

  get canImport(): boolean {
    return !!this.selectedFile && !!this.selectedTemplateId && !this.isImporting;
  }

  onTemplateChange(id: number | null) {
    this.selectedTemplateId = Number.isFinite(id as number) ? (id as number) : null;
    this.selectedTemplate = this.templateTypes.find(t => t.template_id === this.selectedTemplateId) ?? null;
    // default target table
    this.tableName = this.selectedTemplate?.import_table ?? '';
    this.message = '';
  }

  onFileSelect(evt: Event) {
    const file = (evt.target as HTMLInputElement)?.files?.[0] ?? null;
    this.selectedFile = file;
    this.fileName = file ? file.name : '';
    this.message = '';
  }

  importNow() {
    const userId = sessionStorage.getItem('wOSUserId') ?? '';  // 👈 grab the id
    console.log('[ExcelUpload] uploadedBy (userId) =', userId);

    if (!this.selectedFile || !this.selectedTemplate) {
      this.message = 'Select a file and upload type first.';
      return;
    }

    const target = this.tableName?.trim() || this.selectedTemplate.import_table || '';
    if (!target) {
      this.message = 'Target table is required.';
      return;
    }
    this.isImporting = true;
    this.progress = 0;
    this.message = '';
    this.excelSvc
      .commitExcel(this.selectedFile, target, this.token ?? '', userId)
      .subscribe({
        next: (evt: HttpEvent<{ table: string; rows: number; batchId: number }>) => {
          if (evt.type === HttpEventType.UploadProgress && evt.total) {
            this.progress = Math.round((100 * evt.loaded) / evt.total);
          }
          if (evt.type === HttpEventType.Response && evt.body) {
            this.message = `Import complete ✅ Table: ${evt.body.table}, Rows: ${evt.body.rows}, Batch: ${evt.body.batchId ?? '—'}`;
            this.isImporting = false;
            this.progress = -1;
          }
        },
        error: (err) => {
          console.error('Import error', err);
          this.isImporting = false;
          this.progress = -1;
          this.showError(err);
        }
      });
  }

  reset() {
    this.selectedTemplateId = null;
    this.selectedTemplate = null;
    this.selectedFile = null;
    this.fileName = '';
    this.tableName = '';
    this.message = '';
    this.progress = -1;
    this.isImporting = false;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.buildColumnSpec(this.listBatches);
    this.gridApi.sizeColumnsToFit();
    this.loadUXData();
  }

  loadUXData(): void {
    this.excelSvc.getBatchData(this.token!, this.selectedTemplateId!.toString())
      .then((data: any) => {
        console.log("Batch data loaded", data);
        this.rowData = data;
        
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        })
      })
      .catch((err: any) => console.error('Error loading assets:', err));
  }

  buildColumnSpec(table: any): any {
    const tbl = ['batch_id', 'template_type', 'created_at', 'batch_rows', 'added_by_name','actions'];
    const labels: any[] = ['Batch', 'Template Type', 'Created At', 'Batch Rows', 'Added By', ''];
    const alignments: any[] = ['center', 'center', 'left', 'right', 'left', 'center'];
    const widths: any[] = [100, 125, 150, 150, 150, 100];
    const hides: any[] = ['template_type'];
    const expander = 'created_at';

    let i = 1;
    this.columnDefs = tbl.map((col) => {
      let returnedColDef = {};

      const baseColDef = {
        cellClass: (alignments[i - 1] !== 'left') ? ['text-' + alignments[i - 1]] : [],
        field: col,
        headerName: labels[i - 1],
        headerTooltip: labels[i - 1],
        headerClass: 'text-center',
        editable: false,
        sortable: true,
        filter: false,
        resizable: true,
        width: widths[i - 1],
        tooltipField: col,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: (col == expander) ? false : true,
        cellRenderer: this.cellRenderer,
        cellDataType: (col == 'billable') ? 'boolean' : '',
        hide: (hides.includes(col.toLowerCase())) ? true : false
      };

      if (col === 'batch_id') {
        console.log("Batch ID Column")
        returnedColDef = {
          ...baseColDef,
          rowGroup: false,
          hide: false, // show the column
          cellRenderer: (params: any) => {
            // only show date for leaf rows, not group headers
            if (params.node.group) return '';
            return params.value;
          }
        };
      } else if (col === 'created_at') {
        returnedColDef = {
          ...baseColDef,
          cellRenderer: (params: any) => {
            const label = dayjs(params.data.dt_created).format("DD MMM YYYY h:mA");
            return label;
          },
          cellRendererParams: {},
          field: `id`,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true
        };
      } else if (col === 'actions') {
        returnedColDef = {
          ...baseColDef,
          cellRenderer: (params: any) => {
            const copy = document.createElement('span');
            copy.className = 'pointer';
            copy.innerHTML = (params.data.batch_rows > 0) ?
              `<i class="fas fa-fw fa-search c_blue m-1" title="View Data" id="view_${params.data.batch_id}"></i>` : ``;

            copy.addEventListener('click', (e: any) => {
              e.stopPropagation();
              this.showBatchDetails(params.data.batch_id);
            });

            return copy;
          },
          cellRendererParams: {},
          field: `id`,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true
        };
      } else {
        returnedColDef = baseColDef;
      }

      i++;
      return returnedColDef;
    });
  }

  showBatchDetails(batchId: number): void {
    this.excelSvc.getBatchDetail(this.token!, batchId)
      .then((data: any) => {
        console.log("Batch details loaded", data);
        exportFromJSON({
          data: data,
          fileName: `AUMData_Batch_${batchId}_Detail`,
          exportType: 'csv'
        });
      })
      .catch((err: any) => console.error('Error loading batch details:', err));
  }


  onSelectionChanged(event: any): void {
    //if (event.source != "apiSelectAll") { }
    console.log("Selection changed")
  }

  cellRenderer(params: any) {
    let template: any = '';
    switch (params.colDef.field) {
      default:
        template = params.getValue();
        break;
    }
    return template;
  }

  getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    // Apply style to group rows
    if (params.node.group) {
      return {
        backgroundColor: 'var(--welarabonemd)',
        fontWeight: 'bold'
      };
    }

    return undefined;
  };

  private showError(err: any) {
    const msg =
      err?.error?.message ??
      (typeof err?.error === 'string' ? err.error : null) ??
      err?.message ??
      JSON.stringify(err);
    this.message = `Import error: ${msg}`;
  }
}
